import PropTypes from "prop-types";
import React, { Fragment, useEffect, useState } from "react";
import { Typography, Button, Box } from "@mui/material";
import Modal from "../Modal/Modal";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { UploadButton } from "../Buttons";
import ImportIcon from "@mui/icons-material/SaveAlt";
import BulletedList from "../BulletedList/BulletedList";
import { useTheme } from "@mui/styles";
import useLoadingState from "../../hooks/useLoadingState";
import { fetchQuizImportTemplate } from "../../redux/actions/courseBuilderActions";
import { makeGetEntity } from "../../redux/selectors/selectors";
import { enqueueSuccessSnackbar } from "../../redux/actions/snackbarActions";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";
import LoadingSpinnerWithBackdrop from "../LoadingSpinner/LoadingSpinnerWithBackdrop";
import WarningModal from "../WarningModal/WarningModal";
import { pullAt } from "lodash";

const selector = makeGetEntity();

const UploadQuestionsDefaultErrorMessage = ({ isSurvey }) => {
  const { t } = useTranslation();
  let requiredColumns = [
    t("uploadQuestionsModal.columns.questionType"),
    t("uploadQuestionsModal.columns.questionText"),
    t("uploadQuestionsModal.columns.answerOption", { number: 1 }),
    t("uploadQuestionsModal.columns.answerOptionValue", {
      number: 1
    }),
    t("uploadQuestionsModal.columns.answerOption", { number: 2 }),
    t("uploadQuestionsModal.columns.answerOptionValue", {
      number: 2
    })
  ];

  if (isSurvey) {
    requiredColumns = requiredColumns.slice(0, 2);
  }

  return (
    <Box flex={1} display="flex" flexDirection="column" mt={1}>
      <Typography sx={{ pb: 3 }}>
        {t("uploadQuestionsModal.columnsMissingMsg")}
      </Typography>
      <Box display="flex" justifyContent="space-between" pl={0.75} pr={4.5}>
        <BulletedList
          dense
          items={requiredColumns.slice(0, 3).map(column => ({
            itemName: (
              <Typography sx={{ mt: 0.25 }} variant="body2">
                {column}
              </Typography>
            )
          }))}
        />
        <BulletedList
          dense
          items={requiredColumns.slice(3, 6).map(column => ({
            itemName: (
              <Typography sx={{ mt: 0.25 }} variant="body2">
                {column}
              </Typography>
            )
          }))}
        />
      </Box>
    </Box>
  );
};

const UploadQuestionsImportErrorMessage = ({ errors }) => {
  const { t } = useTranslation();
  return (
    <Box flex={1} display="flex" flexDirection="column" mt={1}>
      <Typography sx={{ pb: 3 }}>
        {t("uploadQuestionsModal.rowsNotImportedMsg")}
      </Typography>
      <BulletedList
        dense
        items={errors.map(error => ({
          itemName: (
            <Typography sx={{ mt: 0.25 }} variant="body2">
              {error.detail}
            </Typography>
          )
        }))}
      />
      <Typography sx={{ pb: 3 }}>
        {t("uploadQuestionsModal.checkDataMsg")}
      </Typography>
    </Box>
  );
};

const UploadQuestionsErrorMessage = ({ isSurvey, errors }) => {
  if (Array.isArray(errors) && errors.length > 0) {
    return <UploadQuestionsImportErrorMessage errors={errors} />;
  } else {
    return <UploadQuestionsDefaultErrorMessage isSurvey={isSurvey} />;
  }
};

const ColumnsList = ({ items, maxWidth }) => (
  <Box flex={1} display="flex" justifyContent="center">
    <Box maxWidth={maxWidth}>
      <BulletedList items={items} />
    </Box>
  </Box>
);

const QuestionTypeValues = ({ isSurvey }) => {
  const { t } = useTranslation();

  let questionTypes = [
    { label: t("uploadQuestionsModal.questionTypes.trueFalse"), value: "T/F" },
    {
      label: t("uploadQuestionsModal.questionTypes.multipleChoice"),
      value: "MC"
    },
    { label: t("uploadQuestionsModal.questionTypes.multiSelect"), value: "M-S" }
  ];

  if (isSurvey) {
    questionTypes = [
      ...questionTypes,
      {
        label: t("uploadQuestionsModal.questionTypes.shortAnswer"),
        value: "SA"
      },
      { label: t("uploadQuestionsModal.questionTypes.longAnswer"), value: "LA" }
    ];
  }

  return (
    <>
      (
      {questionTypes.map((type, index) => {
        return (
          <Fragment key={index}>
            {`${type.label}: `}
            <Typography variant="subtitle2" component="span">
              {type.value}
            </Typography>

            {`${index !== questionTypes.length - 1 ? ", " : ""}`}
          </Fragment>
        );
      })}
      )
    </>
  );
};

const getSpreadsheetColumns = (t, isSurvey) => {
  let spreadSheetColumns = [
    {
      itemName: t("uploadQuestionsModal.columns.questionType"),
      secondaryText: <QuestionTypeValues isSurvey={isSurvey} />
    },
    { itemName: t("uploadQuestionsModal.columns.questionText") },
    { itemName: t("uploadQuestionsModal.columns.totalPoints") },
    {
      itemName: "Question Number",
      secondaryText: t("uploadQuestionsModal.questionNumberOptional")
    },

    {
      itemName: t("uploadQuestionsModal.columns.answerOption", { number: 1 }),
      secondaryText:
        isSurvey && t("uploadQuestionsModal.surveyRequiredQuestionTypes")
    },
    {
      itemName: t("uploadQuestionsModal.columns.answerOptionValue", {
        number: 1
      }),
      secondaryText: (
        <>
          {`(${t("uploadQuestionsModal.answerValueCorrectAnswer")}: `}
          <Typography variant="subtitle2" component="span">
            1
          </Typography>
          {`, ${t("uploadQuestionsModal.answerValueIncorrectAnswer")}: `}
          <Typography variant="subtitle2" component="span">
            2
          </Typography>
          )
        </>
      )
    },
    {
      itemName: t("uploadQuestionsModal.columns.answerOption", { number: 2 })
    },
    {
      itemName: t("uploadQuestionsModal.columns.answerOptionValue", {
        number: 2
      })
    },
    {
      itemName: (
        <Typography variant="body1">
          {t("uploadQuestionsModal.columns.moreAnswersIfNeeded")}
        </Typography>
      )
    }
  ];

  if (isSurvey) {
    pullAt(spreadSheetColumns, [2, 5, 7]);
  }

  return spreadSheetColumns;
};

const UploadQuestionsModal = ({ onClose, open, type, onUploadQuestions }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const isFetchingTemplate = useLoadingState("FETCH_QUIZ_IMPORT_TEMPLATE");
  const isImportingQuestions = useLoadingState([
    "IMPORT_QUIZ_QUESTIONS",
    "IMPORT_SURVEY_QUESTIONS"
  ]);
  const [showDownloadBtnSpinner, setShowDownloadBtnSpinner] = useState(false);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errors, setErrors] = useState(null);

  const isSurvey = type === "survey";

  const templateUrl = useSelector(({ courseBuilderState }) => {
    const template = selector(courseBuilderState, {
      type: "quizImportTemplates"
    });
    if (template) {
      return isSurvey ? template.surveyPath : template.quizPath;
    } else {
      return "";
    }
  });

  useEffect(() => {
    if (open && !templateUrl) {
      dispatch(fetchQuizImportTemplate());
    }
  }, [open]);

  useEffect(() => {
    if (!isFetchingTemplate && showDownloadBtnSpinner) {
      setShowDownloadBtnSpinner(false);
      window.location = templateUrl;
    }
  }, [showDownloadBtnSpinner, isFetchingTemplate]);

  const handleDownloadTemplate = () => {
    // If the download button is clicked but the template is still being fetched, we'll show a spinner next to the button until the template is fetched
    if (!templateUrl) {
      setShowDownloadBtnSpinner(true);
    } else {
      window.location = templateUrl;
    }
  };

  const handleUploadQuestions = async event => {
    const target = event.target;
    const file = target.files[0];

    const response = await onUploadQuestions(file);
    const responseErrors = response?.errors || response?.meta?.errors;
    if (responseErrors) {
      setErrors(responseErrors);
      setErrorModalOpen(true);
      target.value = "";
      if (responseErrors.map) {
        responseErrors.map(error => console.log(error));
      } else {
        console.log(responseErrors);
      }
    } else {
      dispatch(
        enqueueSuccessSnackbar({
          message: t(`uploadQuestionsModal.successMsg.${type}`)
        })
      );
      onClose();
    }
  };

  return (
    <>
      <Modal
        disableEnforceFocus
        showCloseIcon
        scrollable
        maxWidth="md"
        dialogTitle={
          <Typography variant="h4" color="textPrimary">
            {t(`uploadQuestionsModal.title.${type}`)}
          </Typography>
        }
        dialogContent={
          <Box display="flex" flexDirection="column">
            <Box mb={5.5} mt={3} display="flex" justifyContent="center">
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={() => handleDownloadTemplate()}
                startIcon={
                  showDownloadBtnSpinner && (
                    <LoadingSpinner
                      size={22}
                      containerHeight="100%"
                      padding={0}
                      color="inherit"
                    />
                  )
                }
              >
                {t("uploadQuestionsModal.downloadBtn")}
              </Button>
            </Box>
            <Box mb={4.5} display="flex" justifyContent="center">
              <Typography variant="h5" color="textSecondary">
                {t("uploadQuestionsModal.orOption")}
              </Typography>
            </Box>
            <Box lineHeight={1.5} px={7}>
              <Typography
                variant="subtitle1"
                color="textSecondary"
                align="center"
              >
                {t("uploadQuestionsModal.createSpreadsheetMsg")}
              </Typography>
            </Box>
            <Box
              display="flex"
              flexWrap="wrap"
              mt={4}
              px={3}
              pb={3}
              color={theme.palette.text.secondary}
            >
              <ColumnsList
                items={getSpreadsheetColumns(t, isSurvey).slice(
                  0,
                  isSurvey ? 3 : 4
                )}
                maxWidth={244}
              />
              <ColumnsList
                items={getSpreadsheetColumns(t, isSurvey).slice(
                  isSurvey ? 3 : 4
                )}
                maxWidth={274}
              />
            </Box>
            {isImportingQuestions && <LoadingSpinnerWithBackdrop />}
            <WarningModal
              open={errorModalOpen}
              onClose={() => setErrorModalOpen(false)}
              hideButtons={true}
              warningMessage={
                <UploadQuestionsErrorMessage
                  isSurvey={isSurvey}
                  errors={errors}
                />
              }
              showCloseIcon
            />
          </Box>
        }
        dialogActions={
          <>
            <Button color="primary" onClick={onClose}>
              {t("uploadQuestionsModal.cancelBtn")}
            </Button>
            <UploadButton
              acceptType=".csv,.xlsx"
              buttonVariant="contained"
              icon={<ImportIcon />}
              buttonText={t("uploadQuestionsModal.uploadBtn")}
              buttonId="uploadQuestions"
              onUpload={e => handleUploadQuestions(e)}
              multiple={false}
            />
          </>
        }
        open={open}
        onClose={onClose}
      />
    </>
  );
};

UploadQuestionsModal.propTypes = {
  /**
   * Called to close the Modal
   */
  onClose: PropTypes.func,
  /**
   * True if the modal is open, false otherwise
   */
  open: PropTypes.bool,
  /**
   * Called when the form is submitted to create the new page
   */
  onSubmit: PropTypes.func,
  type: PropTypes.oneOf(["quiz", "survey"])
};

export default UploadQuestionsModal;
